import { RUNTIME_BACKEND_URL } from '../lib/backendUrl';
import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDataStore } from '../store/dataStore';
import { useAuthStore } from '../store/authStore';
import { formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Plus,
  Users,
  Search,
  Phone,
  Mail,
  Calendar,
  MapPin,
  Trash2,
  Image,
  Loader2,
  FolderOpen,
  Camera,
  Video,
  Plane,
  TreeDeciduous,
  Film,
  BookOpen,
  CreditCard,
  Eye,
  EyeOff,
  Filter,
  Clock,
  Printer,
  CheckCircle2,
  X,
  MessageSquare,
  UserCog
} from 'lucide-react';

const API = RUNTIME_BACKEND_URL;

// Hizmet seçenekleri
const serviceOptions = [
  { key: 'service_photo', label: 'Fotoğraf', icon: Camera },
  { key: 'service_video', label: 'Video', icon: Video },
  { key: 'service_drone', label: 'Drone', icon: Plane },
  { key: 'service_outdoor', label: 'Dış Çekim', icon: TreeDeciduous },
  { key: 'service_clip', label: 'Klip', icon: Film },
  { key: 'service_story', label: 'Hikaye', icon: BookOpen },
];

// Albüm durumu seçenekleri
const STATUS_OPTIONS = [
  { id: 'all', label: 'Aktif', icon: Users, color: 'bg-gray-500' },
  { id: 'no_album', label: 'Albüm Yok', icon: FolderOpen, color: 'bg-gray-400' },
  { id: 'pending', label: 'Beklemede', icon: Clock, color: 'bg-blue-500' },
  { id: 'processing', label: 'İşlemde', icon: Loader2, color: 'bg-amber-500' },
  { id: 'printing', label: 'Baskıda', icon: Printer, color: 'bg-purple-500' },
  { id: 'completed', label: 'Tamamlandı', icon: CheckCircle2, color: 'bg-green-500' },
  { id: 'archived', label: 'Tarihi Geçen', icon: FolderOpen, color: 'bg-slate-500' },
];

// Durum badge renkleri
const getStatusBadge = (status) => {
  const statusMap = {
    pending: { label: 'Beklemede', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
    processing: { label: 'İşlemde', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
    printing: { label: 'Baskıda', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
    completed: { label: 'Tamamlandı', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  };
  return statusMap[status] || { label: 'Albüm Yok', color: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400' };
};

export default function CustomersPage() {
  const navigate = useNavigate();
  const {
    customers,
    customersLoading,
    fetchCustomers,
    addCustomer,
    deleteCustomer,
    venues,
    fetchVenues,
    team,
    fetchTeam
  } = useDataStore();
  const { token, user } = useAuthStore();
  const isTeamMember = user?.role === 'team_member';

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [formData, setFormData] = useState({
    bride_name: '',
    groom_name: '',
    bride_phone: '',
    groom_phone: '',
    email: '',
    wedding_date: '',
    wedding_time_start: '',
    wedding_time_end: '',
    venue_id: '',
    notes: '',
    service_photo: false,
    service_video: false,
    service_drone: false,
    service_outdoor: false,
    service_clip: false,
    service_story: false,
    total_price: '',
    deposit_amount: '',
    assigned_team: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPrices, setShowPrices] = useState(false);

  useEffect(() => {
    fetchCustomers();
    fetchVenues();
    fetchTeam();
  }, [fetchCustomers, fetchVenues, fetchTeam]);

  const resetForm = () => {
    setFormData({
      bride_name: '',
      groom_name: '',
      bride_phone: '',
      groom_phone: '',
      email: '',
      wedding_date: '',
      wedding_time_start: '',
      wedding_time_end: '',
      venue_id: '',
      notes: '',
      service_photo: false,
      service_video: false,
      service_drone: false,
      service_outdoor: false,
      service_clip: false,
      service_story: false,
      total_price: '',
      deposit_amount: '',
      assigned_team: []
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.bride_name || !formData.groom_name || !formData.bride_phone || !formData.wedding_date) {
      toast.error('Lütfen zorunlu alanları doldurun');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        ...formData,
        total_price: parseFloat(formData.total_price) || 0,
        deposit_amount: parseFloat(formData.deposit_amount) || 0
      };
      await addCustomer(payload);
      toast.success('Müşteri eklendi');
      setIsOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (window.confirm(`"${name}" müşterisini silmek istediğinize emin misiniz?`)) {
      try {
        await deleteCustomer(id);
        toast.success('Müşteri silindi');
      } catch (error) {
        toast.error(error.message);
      }
    }
  };

  const handleServiceChange = (key, checked) => {
    setFormData(prev => ({ ...prev, [key]: checked }));
  };

  // Filtreleme: isim, telefon ve durum
  const filteredCustomers = customers.filter(c => {
    // Arama filtresi (isim veya telefon)
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = 
      `${c.bride_name} ${c.groom_name}`.toLowerCase().includes(searchLower) ||
      (c.bride_phone && c.bride_phone.includes(searchTerm)) ||
      (c.groom_phone && c.groom_phone.includes(searchTerm));
    
    if (!matchesSearch) return false;

    // Arşiv filtresi — düğün tarihi geçmiş VEYA albüm tamamlanmış müşteriler
    // En son etkinlik (düğün + nişan/kına/dış çekim vb.) geçene kadar aktif kalır
    const isArchived = (() => {
      const dates = [c.wedding_date, c.event_date, ...(c.events || []).map(e => e.event_date)]
        .filter(Boolean).map(d => new Date(String(d).slice(0, 10)));
      if (!dates.length) return false;
      const last = new Date(Math.max(...dates));
      const today = new Date(); today.setHours(0, 0, 0, 0);
      return last < today;
    })();

    // Aktif = sadece tarihi gelmemiş işler; Tarihi Geçen = geçmişler; durum filtreleri tarihten bağımsız
    if (statusFilter === 'archived') return isArchived;
    if (statusFilter === 'all') return !isArchived;

    const albumStatus = c.album_status || 'no_album';

    if (statusFilter === 'no_album') {
      return !c.has_album;
    }

    return albumStatus === statusFilter;
  });

  const remainingAmount = (parseFloat(formData.total_price) || 0) - (parseFloat(formData.deposit_amount) || 0);

  // Get selected services for a customer
  const getCustomerServices = (customer) => {
    const services = [];
    if (customer.service_photo) services.push('Fotoğraf');
    if (customer.service_video) services.push('Video');
    if (customer.service_drone) services.push('Drone');
    if (customer.service_outdoor) services.push('Dış Çekim');
    if (customer.service_clip) services.push('Klip');
    if (customer.service_story) services.push('Hikaye');
    return services;
  };

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Müşteriler</h1>
          <p className="text-muted-foreground mt-1">Tüm müşterilerinizi yönetin</p>
        </div>
        {!isTeamMember && (
        <Dialog open={isOpen} onOpenChange={(open) => { setIsOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-customer-btn">
              <Plus className="h-4 w-4 mr-2" />
              Yeni Müşteri
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Yeni Müşteri Ekle</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Kişi Bilgileri */}
              <div className="space-y-4">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Kisi Bilgileri</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Ad *</Label>
                    <Input
                      value={formData.bride_name}
                      onChange={(e) => setFormData({ ...formData, bride_name: e.target.value })}
                      placeholder="Ad"
                      data-testid="customer-bride-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Ad *</Label>
                    <Input
                      value={formData.groom_name}
                      onChange={(e) => setFormData({ ...formData, groom_name: e.target.value })}
                      placeholder="Ad"
                      data-testid="customer-groom-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Tel 1 *</Label>
                    <Input
                      value={formData.bride_phone}
                      onChange={(e) => setFormData({ ...formData, bride_phone: e.target.value })}
                      placeholder="05XX XXX XX XX"
                      data-testid="customer-bride-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tel 2</Label>
                    <Input
                      value={formData.groom_phone}
                      onChange={(e) => setFormData({ ...formData, groom_phone: e.target.value })}
                      placeholder="05XX XXX XX XX"
                      data-testid="customer-groom-phone-input"
                    />
                  </div>
                </div>
              </div>

              {/* Düğün Bilgileri */}
              <div className="space-y-4">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Düğün Bilgileri</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Düğün Tarihi *</Label>
                    <Input
                      type="date"
                      value={formData.wedding_date}
                      onChange={(e) => setFormData({ ...formData, wedding_date: e.target.value })}
                      data-testid="customer-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Düğün Salonu</Label>
                    <Select
                      value={formData.venue_id}
                      onValueChange={(value) => setFormData({ ...formData, venue_id: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Salon seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        {venues.map((venue) => (
                          <SelectItem key={venue.id} value={venue.id}>
                            {venue.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Başlangıç Saati</Label>
                    <Input
                      type="time"
                      value={formData.wedding_time_start}
                      onChange={(e) => setFormData({ ...formData, wedding_time_start: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Bitiş Saati</Label>
                    <Input
                      type="time"
                      value={formData.wedding_time_end}
                      onChange={(e) => setFormData({ ...formData, wedding_time_end: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* Hizmet Seçimleri */}
              <div className="space-y-4">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Hizmetler</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {serviceOptions.map(({ key, label, icon: Icon }) => (
                    <label
                      key={key}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        formData[key] 
                          ? 'bg-primary/10 border-primary' 
                          : 'bg-muted/30 border-border hover:bg-muted/50'
                      }`}
                    >
                      <Checkbox
                        checked={formData[key]}
                        onCheckedChange={(checked) => handleServiceChange(key, checked)}
                      />
                      <Icon className={`h-4 w-4 ${formData[key] ? 'text-primary' : 'text-muted-foreground'}`} />
                      <span className={`text-sm ${formData[key] ? 'font-medium' : ''}`}>{label}</span>
                    </label>
                  ))}
                </div>
                <div className="space-y-2">
                  <Label>Manuel Hizmet</Label>
                  <Input
                    value={formData.custom_services || ''}
                    onChange={(e) => setFormData({ ...formData, custom_services: e.target.value })}
                    placeholder="Listede olmayan ek hizmetler..."
                  />
                </div>
              </div>

              {/* Ekip Ataması */}
              <div className="space-y-4">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                  <UserCog className="h-4 w-4" />
                  Ekip Ataması
                </h3>
                {team && team.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {team.map(m => {
                      const isSel = (formData.assigned_team || []).includes(m.id);
                      return (
                        <label key={m.id}
                          className={`flex items-center gap-2 p-2 border rounded-lg cursor-pointer transition ${isSel ? 'bg-primary/10 border-primary' : 'bg-muted/30 hover:bg-muted/50'}`}
                          data-testid={`customer-team-${m.id}`}>
                          <input
                            type="checkbox"
                            checked={isSel}
                            onChange={(e) => {
                              const cur = formData.assigned_team || [];
                              setFormData({
                                ...formData,
                                assigned_team: e.target.checked
                                  ? [...cur, m.id]
                                  : cur.filter(x => x !== m.id)
                              });
                            }}
                            className="rounded"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{m.name}</p>
                            <p className="text-[10px] text-muted-foreground truncate">
                              {m.role === 'photographer' && 'Fotoğrafçı'}
                              {m.role === 'videographer' && 'Kameraman'}
                              {m.role === 'drone_operator' && 'Drone Op.'}
                              {m.role === 'assistant' && 'Asistan'}
                              {m.can_login && ' · Giriş açık'}
                            </p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground italic">
                    Henüz ekip üyesi yok. Ekip sayfasından ekleyip buradan atayabilirsiniz.
                  </p>
                )}
                <p className="text-[10px] text-muted-foreground">
                  Seçili ekip üyeleri bu müşteriyi kendi hesaplarından görebilirler (fiyat/ödeme bilgisi hariç).
                </p>
              </div>

              {/* Ücret Bilgileri */}
              <div className="space-y-4">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Ücret Bilgileri</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Toplam Tutar (TL)</Label>
                    <Input
                      type="number"
                      value={formData.total_price}
                      onChange={(e) => setFormData({ ...formData, total_price: e.target.value })}
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Kapora (TL)</Label>
                    <Input
                      type="number"
                      value={formData.deposit_amount}
                      onChange={(e) => setFormData({ ...formData, deposit_amount: e.target.value })}
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Kalan Tutar</Label>
                    <div className="h-10 px-3 py-2 rounded-md border bg-muted/50 flex items-center font-semibold text-primary">
                      {remainingAmount.toLocaleString('tr-TR')} TL
                    </div>
                  </div>
                </div>
              </div>

              {/* Notlar */}
              <div className="space-y-2">
                <Label>Notlar</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Ek notlar..."
                  rows={3}
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setIsOpen(false)} className="flex-1">
                  İptal
                </Button>
                <Button type="submit" disabled={isSubmitting} className="flex-1" data-testid="customer-submit-btn">
                  {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Kaydet'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="İsim veya telefon ile ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="customer-search-input"
          />
        </div>
        
        {/* Status Filter */}
        <div className="flex flex-wrap gap-2 items-center" data-testid="status-filter">
          {STATUS_OPTIONS.map((option) => {
            const Icon = option.icon;
            const isActive = statusFilter === option.id;
            return (
              <Button
                key={option.id}
                variant={isActive ? "default" : "outline"}
                size="sm"
                onClick={() => setStatusFilter(option.id)}
                className={`gap-2 ${isActive ? '' : 'text-muted-foreground'}`}
                data-testid={`filter-${option.id}`}
              >
                <Icon className={`h-4 w-4 ${option.id === 'processing' && isActive ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">{option.label}</span>
              </Button>
            );
          })}
          <div className="border-l h-6 mx-1" />
          {!isTeamMember && (
            <Button
              variant={showPrices ? "default" : "outline"}
              size="sm"
              onClick={() => setShowPrices(!showPrices)}
              className="gap-2"
              data-testid="toggle-prices"
            >
              {showPrices ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              <span className="hidden sm:inline">Fiyatlar</span>
            </Button>
          )}
        </div>
      </div>
      
      {/* Results Count */}
      <div className="text-sm text-muted-foreground">
        {filteredCustomers.length} müşteri bulundu
        {statusFilter !== 'all' && (
          <span className="ml-2">
            (Filtre: {STATUS_OPTIONS.find(s => s.id === statusFilter)?.label})
          </span>
        )}
      </div>

      {/* Customers Grid */}
      {customersLoading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : filteredCustomers.length > 0 ? (
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {filteredCustomers.map((customer, index) => {
            const services = getCustomerServices(customer);
            const remaining = customer.remaining_amount || ((customer.total_price || 0) - (customer.deposit_amount || 0) - (customer.paid_amount || 0));
            
            return (
              <Link
                key={customer.id}
                to={`/panel/musteri/${customer.id}`}
                className="block"
              >
                <Card 
                  className={`animate-fade-in stagger-${(index % 5) + 1} cursor-pointer hover:shadow-lg transition-shadow h-full`}
                  data-testid={`customer-card-${customer.id}`}
                >
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between mb-4">
                      <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Users className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex items-center gap-1" onClick={(e) => e.preventDefault()}>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => { e.preventDefault(); navigate(`/panel/album/${customer.id}`); }}
                          className="text-primary hover:text-primary"
                          title="Albüm / Fotoğraflar"
                          data-testid={`album-btn-${customer.id}`}
                        >
                          {customer.has_album ? (
                            <Image className="h-4 w-4" />
                          ) : (
                            <FolderOpen className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => { e.preventDefault(); handleDelete(customer.id, `${customer.bride_name} & ${customer.groom_name}`); }}
                          className="text-destructive hover:text-destructive"
                          data-testid={`delete-customer-${customer.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    <h3 className="font-semibold text-lg mb-2">
                      {customer.bride_name} & {customer.groom_name}
                    </h3>
                    
                    {/* Album Status Badge */}
                    {customer.has_album && (
                      <div className="mb-3">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${getStatusBadge(customer.album_status).color}`}>
                          {customer.album_status === 'processing' && <Loader2 className="h-3 w-3 animate-spin" />}
                          {customer.album_status === 'printing' && <Printer className="h-3 w-3" />}
                          {customer.album_status === 'completed' && <CheckCircle2 className="h-3 w-3" />}
                          {customer.album_status === 'pending' && <Clock className="h-3 w-3" />}
                          {getStatusBadge(customer.album_status).label}
                        </span>
                      </div>
                    )}
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Phone className="h-4 w-4" />
                        <span>Tel 1: {customer.bride_phone || customer.phone}</span>
                      </div>
                      {customer.groom_phone && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Phone className="h-4 w-4" />
                        <span>Tel 2: {customer.groom_phone}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Calendar className="h-4 w-4" />
                      <span>{formatDate(customer.wedding_date)}</span>
                      {customer.wedding_time_start && (
                        <span className="text-xs">({customer.wedding_time_start}{customer.wedding_time_end ? ` - ${customer.wedding_time_end}` : ''})</span>
                      )}
                    </div>
                    {customer.venue_name && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <MapPin className="h-4 w-4" />
                        <span>{customer.venue_name}</span>
                      </div>
                    )}
                  </div>

                  {/* Services */}
                  {services.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1">
                      {services.map(service => (
                        <span key={service} className="px-2 py-0.5 text-xs rounded-full bg-primary/10 text-primary">
                          {service}
                        </span>
                      ))}
                      {customer.custom_services && (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                          {customer.custom_services}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Price Info - Ekip üyesine gösterme */}
                  {!isTeamMember && showPrices && (customer.total_price > 0 || remaining > 0) && (
                    <div className="mt-3 p-3 rounded-lg bg-muted/50 space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Toplam:</span>
                        <span>{(customer.total_price || 0).toLocaleString('tr-TR')} TL</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Kapora:</span>
                        <span>{(customer.deposit_amount || 0).toLocaleString('tr-TR')} TL</span>
                      </div>
                      <div className="flex justify-between text-sm font-semibold border-t border-border pt-1">
                        <span className="text-primary flex items-center gap-1">
                          <CreditCard className="h-3 w-3" /> Kalan:
                        </span>
                        <span className="text-primary">{remaining.toLocaleString('tr-TR')} TL</span>
                      </div>
                    </div>
                  )}

                  {/* Selection Completed */}
                  {customer.selection_completed && (
                    <div className="mt-2 p-1.5 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                      <p className="text-[11px] text-green-700 dark:text-green-400 font-medium">
                        ✓ Seçim Tamamlandı ({customer.selected_count} fotoğraf)
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
              </Link>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12">
          <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground/30" />
          <h3 className="text-lg font-medium mb-2">Henüz müşteri yok</h3>
          <p className="text-muted-foreground mb-4">Yeni müşteri ekleyerek başlayın</p>
        </div>
      )}
    </div>
  );
}
